import { useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { writeExpectingRows } from "@/lib/supabase-write";
import type { IndicacaoAgencia } from "@/types/indicacao-agencia";

const QUERY_KEY = ["indicacoes-agencia"] as const;

/**
 * Busca as marcas de indicação VIVAS (deleted_at IS NULL). A tabela é pequena
 * (curadoria manual); remoção é soft delete (migration 149) — o rastro
 * continua na Timeline da Agência, mas some da listagem/curadoria.
 */
export function useIndicacoesAgencia() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<IndicacaoAgencia[]> => {
      const { data, error } = await supabase
        .from("freelancer_agencia_indicacoes")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as IndicacaoAgencia[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Filtra as marcas de um freelancer específico (deriva do cache da query geral). */
export function useIndicacoesDoFreelancer(freelancerId: string | undefined) {
  const { data, ...rest } = useIndicacoesAgencia();
  const indicacoes = useMemo(
    () =>
      (data ?? []).filter((indicacao) => indicacao.freelancer_id === freelancerId),
    [data, freelancerId],
  );
  return { ...rest, data, indicacoes };
}

export interface UpsertIndicacoesInput {
  freelancer_id: string;
  /** Uma ou mais agências que dão match com o freelancer. */
  agencia_ids: string[];
  motivo?: string | null;
}

/**
 * Cria/atualiza marcas de match (1 linha viva por par freelancer+agência) para
 * uma ou mais agências de uma vez. `tipo` é sempre 'indicado'.
 *
 * NÃO usa upsert/ON CONFLICT: a unicidade do par agora é um índice único
 * PARCIAL (`uq_fai_par_ativo`, migration 149, só entre linhas com
 * deleted_at IS NULL) para permitir re-indicar depois de um soft delete.
 * Testado: `INSERT ... ON CONFLICT (freelancer_id, agencia_id)` sem WHERE não
 * infere um índice parcial (Postgres exige o mesmo WHERE no próprio ON
 * CONFLICT), e o supabase-js `.upsert()` não tem como passar esse WHERE. Por
 * isso resolvemos manualmente: busca o que já está ativo para o freelancer
 * entre as agências pedidas, UPDATE (edita motivo) nos pares já ativos,
 * INSERT (match novo ou re-indicação) nos demais.
 */
export function useUpsertIndicacoes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertIndicacoesInput) => {
      const agenciaIds = [...new Set(input.agencia_ids)].filter(Boolean);
      if (agenciaIds.length === 0) return;
      const motivo = input.motivo?.trim() ? input.motivo.trim() : null;
      const agora = new Date().toISOString();

      const { data: ativos, error: erroAtivos } = await supabase
        .from("freelancer_agencia_indicacoes")
        .select("id, agencia_id")
        .eq("freelancer_id", input.freelancer_id)
        .in("agencia_id", agenciaIds)
        .is("deleted_at", null);
      if (erroAtivos) throw erroAtivos;

      const idAtivoPorAgencia = new Map(
        (ativos ?? []).map((linha) => [linha.agencia_id, linha.id]),
      );

      const atualizacoes = agenciaIds
        .filter((agenciaId) => idAtivoPorAgencia.has(agenciaId))
        .map((agenciaId) =>
          writeExpectingRows(
            supabase
              .from("freelancer_agencia_indicacoes")
              .update({ motivo, updated_at: agora })
              .eq("id", idAtivoPorAgencia.get(agenciaId) as string),
            "atualizar motivo do match",
          ),
        );

      const agenciasNovas = agenciaIds.filter(
        (agenciaId) => !idAtivoPorAgencia.has(agenciaId),
      );
      const insercao =
        agenciasNovas.length > 0
          ? writeExpectingRows(
              // created_by é server-set (DEFAULT auth.uid(), migration 157) —
              // não mandamos do cliente para não ser forjável.
              supabase.from("freelancer_agencia_indicacoes").insert(
                agenciasNovas.map((agencia_id) => ({
                  freelancer_id: input.freelancer_id,
                  agencia_id,
                  tipo: "indicado" as const,
                  motivo,
                  updated_at: agora,
                })),
              ),
              "criar match",
            )
          : Promise.resolve([]);

      await Promise.all([...atualizacoes, insercao]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Remove uma marca de indicação pelo id (soft delete: marca `deleted_at`, não
 * apaga a linha — o trigger `trg_freelancer_event_on_radar_indicacao`
 * (migration 149) emite `indicacao_removida` na Timeline da Agência ao ver
 * essa transição).
 */
export function useRemoveIndicacao() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await writeExpectingRows(
        supabase
          .from("freelancer_agencia_indicacoes")
          .update({ deleted_at: new Date().toISOString() })
          .eq("id", id)
          .is("deleted_at", null),
        "remover match",
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
